import React from 'react';
import { Table } from '@tanstack/table-core';
import classNames from 'classnames';

import ColumnCell from './column-cell';

type IProps = {
  table: Table<any>;
  onRowClick?: ((row: any) => void) | null;
  isLoading: boolean;
  selectedRowIds: (string | number)[];
  selectedRowIdsSet?: Set<string | number>;
  onRowSelectHandler: (rowId: number, isSelect: boolean) => void;
  maxSelectedRowsLimit?: number | null;
  isSelectAllAcrossPages?: boolean;
  page?: number;
  pageSize?: number;
  appliedLimit?: number | null;
  enableCappedSelectAllUI?: boolean;
  cappedSelectAllTooltipText?: string;
  allowIndivualRowDeSelectionAcrossAllPage?: boolean;
  /**
   * When true, suppress the "all rows checked" visual shortcut even when
   * `isSelectAllAcrossPages` is active. Used for custom-limit mode where
   * the banner shows N but no individual row should appear checked.
   * Defaults to false (standard select-all behaviour preserved).
   */
  isCustomLimitMode?: boolean;
  cellRenderer?: React.FC<any> | null;
  isRowDisabled?: ((row: any) => boolean) | null;
  getRowDisabledTooltip?: ((row: any) => string) | null;
  enableRowSelection?: boolean;
  enableExpandedRows?: boolean;
  getRowClassName?: ((row: any) => string | undefined) | null;
};

type TableRowProps = {
  row: any;
  table: Table<any>;
  CellComponent: React.FC<any>;
  rowClassName: string;
  isRowSelected: boolean;
  isDisabled: boolean;
  isLoading: boolean;
  enableRowSelection: boolean;
  isRowExpanded: boolean;
  onRowClick?: ((row: any) => void) | null;
  onRowSelectHandler: (rowId: number, isSelect: boolean) => void;
  getRowDisabledTooltip?: ((row: any) => string) | null;
};

// One <tr> worth of cells, memoized so a selection change only re-renders the
const TableRowComponent: React.FC<TableRowProps> = ({
  row,
  table,
  CellComponent,
  rowClassName,
  isRowSelected,
  isDisabled,
  isLoading,
  enableRowSelection,
  isRowExpanded,
  onRowClick,
  onRowSelectHandler,
  getRowDisabledTooltip,
}) => {
  const cells = row.getVisibleCells();
  return (
    <div className={rowClassName}>
      {cells.map((cell: any, index: number) => (
        <CellComponent
          key={cell.id || `cell-${row.id}-${index}`}
          table={table}
          row={row}
          cell={cell}
          cellIndex={index}
          onRowClick={onRowClick}
          isRowSelected={isRowSelected}
          onRowSelectHandler={onRowSelectHandler}
          isDisabled={isDisabled}
          getRowDisabledTooltip={getRowDisabledTooltip}
          rowData={row.original}
          isLoading={isLoading}
          enableRowSelection={enableRowSelection}
          isRowExpanded={isRowExpanded}
        />
      ))}
    </div>
  );
};

const TableRow = React.memo(
  TableRowComponent,
  (prev, next) =>
    prev.row.id === next.row.id &&
    prev.row.original === next.row.original &&
    prev.isRowSelected === next.isRowSelected &&
    prev.isDisabled === next.isDisabled &&
    prev.rowClassName === next.rowClassName &&
    prev.isLoading === next.isLoading &&
    prev.enableRowSelection === next.enableRowSelection &&
    prev.isRowExpanded === next.isRowExpanded &&
    prev.onRowClick === next.onRowClick &&
    prev.onRowSelectHandler === next.onRowSelectHandler &&
    prev.CellComponent === next.CellComponent &&
    prev.table === next.table,
  // getRowDisabledTooltip is intentionally excluded: it's only invoked lazily
  // on hover (same as ColumnCell's memo), and its identity changes with every
  // selection — comparing it would defeat the memo.
);

const TBody: React.FC<IProps> = ({
  table,
  onRowClick,
  isLoading,
  selectedRowIds,
  selectedRowIdsSet,
  onRowSelectHandler,
  maxSelectedRowsLimit = null,
  isSelectAllAcrossPages = false,
  page = 1,
  pageSize = 0,
  appliedLimit = null,
  enableCappedSelectAllUI = false,
  cappedSelectAllTooltipText = '',
  allowIndivualRowDeSelectionAcrossAllPage = false,
  isCustomLimitMode = false,
  cellRenderer,
  isRowDisabled = null,
  getRowDisabledTooltip = null,
  enableRowSelection = true,
  enableExpandedRows = false,
  getRowClassName = null,
}) => {
  const CellComponent = cellRenderer || ColumnCell;
  const { rows } = table?.getRowModel() || {};

  // If loading and no rows, create placeholder rows
  const displayRows =
    isLoading && rows?.length === 0
      ? Array.from({ length: 6 }, (_, i) => ({
          id: `loading-row-${i}`,
          original: { id: `loading-row-${i}` },
          getVisibleCells: () =>
            table
              .getAllColumns()
              .filter((col) => col.getIsVisible())
              .map((col, idx) => ({
                id: `loading-cell-${i}-${idx}`,
                column: col,
                row: { original: { id: `loading-row-${i}` } },
                getContext: () => ({}),
              })),
        }))
      : rows;

  // Use Set for O(1) lookup if provided, fallback to array includes for backward compatibility
  const checkIsRowSelected = (rowId: string | number | undefined) => {
    if (rowId === undefined) return false;
    return selectedRowIdsSet
      ? selectedRowIdsSet.has(rowId)
      : selectedRowIds.includes(rowId);
  };

  const isCappedSelectAllActive =
    enableCappedSelectAllUI &&
    isSelectAllAcrossPages &&
    appliedLimit != null &&
    appliedLimit > 0 &&
    pageSize > 0;

  return (
    <div className="tanstack-table--tbody">
      {displayRows.map((row, rowIndex) => {
        const isPlaceholderRow = isLoading && rows.length === 0;

        const rowDepth =
          enableExpandedRows && !isPlaceholderRow && (row as any).depth != null
            ? (row as any).depth
            : 0;
        const trClassName = classNames(
          'tanstack-table--tr',
          rowDepth > 0 && 'tanstack-table--tr--sub',
        );
        const rowClassName = [
          'tanstack-table--tr',
          trClassName,
          !isLoading && getRowClassName
            ? getRowClassName(row.original)
            : undefined,
        ]
          .filter(Boolean)
          .join(' ');

        // These values are row-level (they depend on the row, not the cell),
        // so they are computed once here and passed to the memoized row.
        const rowId = row.original?.id;

        // When select all across pages is active:
        // - If allowIndivualRowDeSelectionAcrossAllPage is true, check
        //   selectedRowIds for actual state
        // - Otherwise, all rows are considered selected
        let isRowSelected = false;
        if (!isPlaceholderRow) {
          if (allowIndivualRowDeSelectionAcrossAllPage && isSelectAllAcrossPages) {
            isRowSelected = checkIsRowSelected(rowId);
          } else if (isCappedSelectAllActive) {
            const rowStartIndex = (page - 1) * pageSize;
            const globalIndex = rowStartIndex + rowIndex;
            isRowSelected = globalIndex < appliedLimit;
          } else if (isSelectAllAcrossPages && !isCustomLimitMode) {
            isRowSelected = true;
          } else {
            isRowSelected = checkIsRowSelected(rowId);
          }
        }

        const isRowDisabledByFunction = Boolean(
          !isPlaceholderRow && isRowDisabled && isRowDisabled(row.original),
        );

        const isBeyondAppliedLimit = (() => {
          if (!isCappedSelectAllActive) return false;
          const rowStartIndex = (page - 1) * pageSize;
          const globalIndex = rowStartIndex + rowIndex;
          return globalIndex >= (appliedLimit || 0);
        })();

        // Allow deselection in "select all across pages" mode if prop is enabled
        const isDisabled = Boolean(
          (isSelectAllAcrossPages &&
            !allowIndivualRowDeSelectionAcrossAllPage) ||
            isRowDisabledByFunction ||
            (isBeyondAppliedLimit && !isRowSelected) ||
            (!isRowSelected &&
              maxSelectedRowsLimit &&
              selectedRowIds.length >= maxSelectedRowsLimit),
        );

        const resolvedTooltip =
          !isPlaceholderRow && isBeyondAppliedLimit
            ? () => cappedSelectAllTooltipText
            : getRowDisabledTooltip;

        const isRowExpanded =
          enableExpandedRows && !isPlaceholderRow ? row.getIsExpanded() : false;

        return (
          <TableRow
            key={row.id || `row-${rowIndex}`}
            row={row}
            table={table}
            CellComponent={CellComponent}
            rowClassName={rowClassName}
            isRowSelected={isRowSelected}
            isDisabled={isDisabled}
            isLoading={isLoading}
            enableRowSelection={enableRowSelection}
            isRowExpanded={isRowExpanded}
            onRowClick={onRowClick}
            onRowSelectHandler={onRowSelectHandler}
            getRowDisabledTooltip={resolvedTooltip}
          />
        );
      })}
    </div>
  );
};

export default TBody;
